import productsServiceCommon, {
  isType,
  isExtendable,
  generateProppingImageResources,
} from '../';
import constants from '../../../settings/constants';
import { getProppingBounds } from '../models';
import { FILTERS, ITEMS, MISSING_PRODUCT_CATEGORIES } from '../../../constants';
import { colorFilterObject } from '../itemsFilters';

const MOUNTING_RAIL_WIDTH_SHORT = 650;
const MOUNTING_RAIL_WIDTH_LONG = 1250;

const articleImages = [];

/**
 * See {@link getFilters} for docs on filters structure.
 * @type {[{filter: (function(*=): function({product: *}): boolean), condition: (function(): boolean), Component: () => JSX.Element}]}
 */
const getFilters = () => [colorFilterObject({ appliesTo: [FILTERS.PARTS] })];

function makeArticleImages() {
  const imageList = productsServiceCommon
    .getAll()
    .filter(
      item =>
        item.modelid && !productsServiceCommon.isType(item, ITEMS.SIDEWALL)
    )
    .map(product => {
      const { color } = product.filter;

      const proppingBounds = getProppingBounds(product);
      const proppingName = proppingBounds.length > 0 ? 'propping_1_' : '';

      const sceneFileName = `${product.modelid}_${color}_0001.png`;
      const menuFileName = `${product.modelid}_${proppingName}${color}_t_0001.png`;

      const hasPortraitSpecificThumb = productsServiceCommon.isType(
        product,
        ITEMS.SECTION
      );

      return {
        name: product.id,
        color: color,
        type: product.filter.type,
        modelid: product.modelid,
        proppingBounds,
        url: `${constants.IMAGE_ROOT}${sceneFileName}`,
        thumbnailUrl: `${constants.IMAGE_ROOT}thumbnails/${menuFileName}`,
        thumbnailUrlPortrait: hasPortraitSpecificThumb
          ? `${constants.IMAGE_ROOT}thumbnails/portrait/${menuFileName}`
          : null,
      };
    });

  articleImages.push(...generateProppingImageResources(imageList));
}

function getDragMode(product) {
  return constants.DRAG_MODE.FLOAT;
}

function getArticleImage(id) {
  return articleImages.find(image => image.name === id);
}

function isMultiParentProduct(item) {
  return (
    productsServiceCommon.isType(item, ITEMS.CLOTHES_RAIL) &&
    productsServiceCommon.isExtendable(item)
  );
}

function isClothesRail(item) {
  return productsServiceCommon.isType(item, ITEMS.CLOTHES_RAIL);
}

function isDependentItem(item) {
  return (
    productsServiceCommon.isType(item, ITEMS.MOUNTING_RAIL) ||
    productsServiceCommon.isType(item, ITEMS.SIDEWALL)
  );
}

function isSection(item) {
  return productsServiceCommon.isType(item, ITEMS.SECTION);
}

function isInsert(item) {
  return !isSection(item) && !isDependentItem(item);
}

function getFit(item, slot) {
  let fits;

  switch (item.filter.type) {
    case ITEMS.CLOTHES_RAIL:
      const crs = productsServiceCommon.getFilteredItems(isClothesRail, {
        color: slot.color || item.filter.color,
      });

      if (slot.partnerSlot) {
        const extRails = crs.filter(product => isExtendable(product));
        const extWidth = slot.partnerSlot.x + slot.partnerSlot.width - slot.x;
        fits = extRails.map(rail => ({ ...rail, width: extWidth }));
      } else {
        fits = crs;
      }
      break;

    case ITEMS.SHELF:
    case ITEMS.BASKET:
    case ITEMS.DRAWER:
    case ITEMS.SHOE_SHELF:
      fits = productsServiceCommon.getFilteredItems(
        product => isType(product, item.filter.type),
        {
          color: slot.color || item.filter.color,
        }
      );
      break;
    default:
      return item;
  }

  if (fits?.length) {
    return Object.assign({}, fits[0]);
  }
}

function shouldHaveColorSelector(item) {
  return productsServiceCommon.isType(item, [
    ITEMS.BASKET,
    ITEMS.SHELF,
    ITEMS.SHOE_SHELF,
    ITEMS.CLOTHES_RAIL,
    ITEMS.DRAWER,
    ITEMS.SECTION,
  ]);
}

function shouldHaveChildColorSelector(item) {
  return (
    isSection(item) &&
    item.items.some(
      child => shouldHaveColorSelector(child) && !isMultiParentProduct(child)
    )
  );
}

function getInitialZPos(item) {
  if (isSection(item)) {
    return 20;
  }
  return 0;
}

function isMountingRail(item) {
  return !!productsServiceCommon.isType(item, ITEMS.MOUNTING_RAIL);
}

function isShortMountingRail(item) {
  return isMountingRail(item) && item.width === MOUNTING_RAIL_WIDTH_SHORT;
}

function isLongMountingRail(item) {
  return isMountingRail(item) && item.width === MOUNTING_RAIL_WIDTH_LONG;
}

function isSidePanel(item) {
  return !!productsServiceCommon.isType(item, ITEMS.SIDE_PANEL);
}

function isEndShelf(item) {
  return !!productsServiceCommon.isType(item, ITEMS.END_SHELF);
}

function getMandatoryProductAvailabilityPerColor(colors) {
  const productAvailabilityPerColor = {};
  colors.forEach(color => {
    const filteredProductOfThisColor = productsServiceCommon.getFilteredItems(
      Boolean,
      { color }
    );
    productAvailabilityPerColor[color] = {
      shortMountingRailAvailable: !!filteredProductOfThisColor.find(prod =>
        isShortMountingRail(prod)
      ),
      longMountingRailAvailable: !!filteredProductOfThisColor.find(prod =>
        isLongMountingRail(prod)
      ),
      sidePanelAvailable: !!filteredProductOfThisColor.find(prod =>
        isSidePanel(prod)
      ),
      endShelfAvailable: !!filteredProductOfThisColor.find(prod =>
        isEndShelf(prod)
      ),
    };
  });

  return productAvailabilityPerColor;
}

function hasAllMandatoryProductsOfColor(color) {
  const availability = getMandatoryProductAvailabilityPerColor([color]);

  return (
    availability[color].shortMountingRailAvailable &&
    availability[color].longMountingRailAvailable &&
    availability[color].sidePanelAvailable &&
    availability[color].endShelfAvailable
  );
}

function getMissingMandatoryProductCategories() {
  const sections = productsServiceCommon.getFilteredItems(item =>
    isSection(item)
  );

  const colors = ['white', 'dark_grey'];
  const availabilityPerColor = getMandatoryProductAvailabilityPerColor(colors);

  const sectionAvailable = sections.length > 0;
  const shortMountingRailAvailable = colors.some(
    color => availabilityPerColor[color].shortMountingRailAvailable
  );
  const longMountingRailAvailable = colors.some(
    color => availabilityPerColor[color].longMountingRailAvailable
  );
  const sidePanelAvailable = colors.some(
    color => availabilityPerColor[color].sidePanelAvailable
  );
  const endShelfAvailable = colors.some(
    color => availabilityPerColor[color].endShelfAvailable
  );
  const matchingColorsAvailable = colors.some(color =>
    hasAllMandatoryProductsOfColor(color)
  );

  const missingProductCategories = [];
  if (!sectionAvailable)
    missingProductCategories.push(MISSING_PRODUCT_CATEGORIES.SECTION);
  if (!shortMountingRailAvailable)
    missingProductCategories.push(
      MISSING_PRODUCT_CATEGORIES.MOUNTING_RAIL_SHORT
    );
  if (!longMountingRailAvailable)
    missingProductCategories.push(
      MISSING_PRODUCT_CATEGORIES.MOUNTING_RAIL_LONG
    );
  if (!sidePanelAvailable)
    missingProductCategories.push(MISSING_PRODUCT_CATEGORIES.SIDE_PANEL);
  if (!endShelfAvailable)
    missingProductCategories.push(MISSING_PRODUCT_CATEGORIES.END_SHELF);
  if (
    shortMountingRailAvailable &&
    longMountingRailAvailable &&
    sidePanelAvailable &&
    endShelfAvailable &&
    !matchingColorsAvailable
  )
    missingProductCategories.push(
      MISSING_PRODUCT_CATEGORIES.MOUNTING_RAILS_SIDE_PANEL_END_SHELF_COLOR_MISMATCH
    );

  return missingProductCategories;
}

function isAdjustable(item) {
  return productsServiceCommon.isExtendable(item);
}

export default {
  articleImages,
  getArticleImage,
  getDragMode,
  getFit,
  getInitialZPos,
  getMissingMandatoryProductCategories,
  hasAllMandatoryProductsOfColor,
  isAdjustable,
  isDependentItem,
  isMultiParentProduct,
  isInsert,
  isSection,
  isClothesRail,
  makeArticleImages,
  shouldHaveColorSelector,
  shouldHaveChildColorSelector,
  getFilters,
};
